import { useEffect, useRef } from "react";
import { cn } from "../../../lib/utils";

export type MenuBoxOptionBase = { key: string };
export type MenuBoxRenderItem<T> = (
  option: T,
  context: { isSelected: boolean; className: string }
) => JSX.Element;
interface SuccessMenuBoxData<T extends MenuBoxOptionBase> {
  type: "success";
  data: T[];
}

interface ErrorMenuBoxData {
  type: "error";
  message: string;
}
interface NoteMenuBoxData {
  type: "note";
  message: string;
}

type MenuBoxData<T extends MenuBoxOptionBase> =
  SuccessMenuBoxData<T> | ErrorMenuBoxData | NoteMenuBoxData;

export type RenderContentLayout = (params: {
  loadingJsx: React.ReactNode;
  itemsJsx: React.ReactNode;
  isLoading: boolean;
  error: Error | null;
  className: string;
}) => React.ReactNode;

export interface MenuBoxProps<T extends MenuBoxOptionBase> {
  data: MenuBoxData<T>;
  selectedIndex: number | null;
  selectOptionAndCleanUp: (option: T) => void;
  setHighlightedIndex: (index: number) => void;
  isLoading?: boolean;
  error?: Error | null;
  renderContentLayout?: RenderContentLayout;
  renderItem?: MenuBoxRenderItem<T>;
  showLoading?: boolean;
  className?: string;
  contentClassName?: string;
}

export function MenuBox<T extends MenuBoxOptionBase>({
  data,
  selectedIndex,
  selectOptionAndCleanUp,
  setHighlightedIndex,
  isLoading,
  renderContentLayout,
  error,
  showLoading,
  className,
  renderItem,
  contentClassName,
}: MenuBoxProps<T>) {
  function renderData() {
    switch (data.type) {
      case "success": {
        if (!isLoading && data.data.length === 0) {
          return (
            <div
              className={cn(
                "text-gray-500 text-center text-[14px] p-2",
                contentClassName
              )}
            >
              No results
            </div>
          );
        }
        return (
          <div className={cn("flex-1 overflow-y-auto", contentClassName)}>
            {data.data.map((option, i: number) => (
              <MentionsTypeaheadMenuItem
                index={i}
                isSelected={selectedIndex === i}
                onSelect={() => {
                  setHighlightedIndex(i);
                  selectOptionAndCleanUp(option);
                }}
                onMouseEnter={() => {
                  setHighlightedIndex(i);
                }}
                key={option.key}
                option={option}
                renderItem={
                  renderItem
                    ? renderItem
                    : (opt, { className }) => (
                        <span className={className}>{opt.key}</span>
                      )
                }
              />
            ))}
          </div>
        );
      }

      case "error": {
        return (
          <div className={cn("text-red-500 text-center p-2", contentClassName)}>
            Error: {data.message}
          </div>
        );
      }
      case "note": {
        return (
          <div
            className={cn(
              "text-gray-400 text-center text-xl mt-2",
              contentClassName
            )}
          >
            {data.message}
          </div>
        );
      }
      default: {
        return null;
      }
    }
  }
  const loadingJsx: React.ReactNode = showLoading !== false &&
    data.type === "success" && (
      <div className="w-full h-[20px] relative">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/40">
            <i className="fas fa-spinner fa-spin text-[14px] text-gray-500"></i>
          </div>
        )}
      </div>
    );
  const itemsJsx: React.ReactNode = renderData();
  const baseClassName = cn(
    `w-[240px] max-h-[208px] bg-white shadow-md rounded-[8px] relative py-[8px] border 
        border-neutral-200 z-[1065] font-(family-name:--font-inter) text-[#333333]`,
    className
  );
  const content = renderContentLayout ? (
    renderContentLayout({
      loadingJsx,
      itemsJsx,
      isLoading: !!isLoading,
      error: error || null,
      className: baseClassName,
    })
  ) : (
    <div className={baseClassName}>
      {loadingJsx}
      {itemsJsx}
    </div>
  );
  return content;
}

function MentionsTypeaheadMenuItem<T extends MenuBoxOptionBase>({
  isSelected,
  onSelect,
  onMouseEnter,
  option,
  renderItem,
}: {
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
  option: T;
  renderItem: MenuBoxRenderItem<T>;
}) {
  const itemRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({
        block: "nearest",
      });
    }
  }, [isSelected]);
  return (
    <div
      ref={itemRef}
      key={option.key}
      tabIndex={-1}
      onMouseEnter={onMouseEnter}
      onMouseDown={onSelect}
      className="flex"
    >
      {renderItem(option, {
        isSelected,
        className: `${isSelected ? "bg-blue-100" : "bg-white"} flex-1  px-[12px] py-[6px] cursor-pointer`,
      })}
    </div>
  );
}
